import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CampaignsPage.css';
import { AVAILABLE_CITIES_RU, cityToSlug } from './cityRouting';
import { authFetch, clearAuthSession } from './authSession';

import { API_BASE } from './config/api';

const formatDate = (value) => {
  if (!value) return 'Не указан';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [selectedCities, setSelectedCities] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('authToken');

  const totalCitiesSelected = useMemo(() => selectedCities.length, [selectedCities]);

  useEffect(() => {
    if (!token) {
      navigate('/auth', { replace: true });
      return;
    }
    loadCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const loadCampaigns = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Не удалось загрузить кампании');
      }
      setCampaigns(data);
    } catch (e) {
      setError(e.message || 'Ошибка загрузки кампаний');
    } finally {
      setIsLoading(false);
    }
  };

  const createCampaign = async (event) => {
    event.preventDefault();
    if (!newCampaignName.trim()) {
      setError('Введите название кампании');
      return;
    }
    if (selectedCities.length === 0) {
      setError('Выберите хотя бы один город');
      return;
    }
    if (!startDate || !endDate) {
      setError('Укажите период проведения кампании');
      return;
    }
    if (startDate > endDate) {
      setError('Дата начала не может быть позже даты окончания');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      let selectedCitiesNames = selectedCities.map((city) => city[0]);
      console.log("selectedCitiesNames : ", selectedCitiesNames)
      console.log("selectedCities : ", selectedCities)

      const response = await authFetch(`${API_BASE}/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      
        body: JSON.stringify({
          name: newCampaignName.trim(),
          cityNames: selectedCitiesNames, //selectedCities,
          startDate,
          endDate,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Не удалось создать кампанию');
      }
      setNewCampaignName('');
      setSelectedCities([]);
      setStartDate('');
      setEndDate('');
      navigate(`/${data.publicId}/${cityToSlug(selectedCities[0])}`);
    } catch (e) {
      setError(e.message || 'Ошибка создания кампании');
    } finally {
      setIsLoading(false);
    }
  };

  const openCampaign = (campaign) => {
    if (campaign.cityNames && campaign.cityNames.length > 0) {
      navigate(`/${campaign.publicId}/${cityToSlug(campaign.cityNames[0])}`);
    } else {
      setError('Кампания не содержит городов');
    }
  };

  const logout = () => {
    clearAuthSession();
    navigate('/auth', { replace: true });
  };

  return (
    <div className="campaigns-page">
      <div className="campaigns-card">
        <div className="campaigns-head">
          <div>
            <p className="campaigns-kicker">
              <svg viewBox="0 0 24 21" fill="none" aria-hidden>
                <path
                  d="M12.8252 0.00553429C13.5238 0.00466141 14.2681 -0.00864774 14.9629 0.00944054L16.3281 1.35612C17.1603 1.40348 18.2089 1.37365 19.0596 1.3737L23.9785 1.37565L23.9766 4.94206C23.9778 5.8302 24.1586 6.97551 23.4893 7.63444C23.2976 7.82423 23.0604 7.96164 22.8008 8.03385C22.3898 8.14532 20.5827 8.09134 20.0332 8.10026L20.042 13.6149C19.0755 14.4848 17.9843 15.3355 17.0127 16.2087C16.6798 16.5079 16.0904 16.9461 15.8027 17.235C15.7804 18.1798 15.7912 19.1929 15.7988 20.1413C14.4341 20.1442 13.0334 20.1256 11.6738 20.1462C11.6744 19.2108 11.6564 18.2089 11.6807 17.279L5.28516 17.277C5.06517 18.1646 4.80853 19.2789 4.5459 20.1423H0C0.234441 19.011 0.43135 17.7061 0.616211 16.5612L1.29395 12.4294L1.77734 9.49772C1.84715 9.0903 1.99566 8.38845 2.03711 8.01042C2.15221 7.94336 2.88744 7.9723 3.08008 7.97233H5.0957C6.79086 7.97211 8.61717 7.93918 10.3027 7.98014C10.8741 7.36907 11.4878 6.7869 12.0742 6.1901C12.2984 5.96199 12.6176 5.67045 12.8242 5.43717C12.83 3.64114 12.8449 1.80052 12.8252 0.00553429ZM19.9941 14.4958C20.0663 14.5668 20.041 15.7394 20.041 15.9118L20.043 18.8512C20.0417 19.1121 20.0171 19.9191 20.0537 20.1432H16.8789C16.7575 20.1477 16.6823 20.1563 16.5645 20.1198C16.4902 19.8305 16.5485 17.868 16.5488 17.4479C16.997 17.0301 17.5495 16.5891 18.0234 16.1911C18.6872 15.634 19.3441 15.0688 19.9941 14.4958ZM8.87988 17.9049C8.85453 18.6081 8.87522 19.4291 8.87793 20.1413L8.28516 20.1432L5.22754 20.1393C5.4198 19.5931 5.66498 18.5256 5.83789 17.9079L8.87988 17.9049ZM18.2441 3.12663C17.6785 3.12759 17.0846 3.13804 16.5215 3.12858L16.5195 4.27702L18.2432 4.27311L18.2441 3.12663Z"
                  fill="currentColor"
                />
              </svg>
              Купи.баннер
            </p>
            <h2>Кампании</h2>
          </div>
          <button type="button" className="logout-btn" onClick={logout}>Выйти</button>
        </div>

        <form className="create-campaign-form" onSubmit={createCampaign}>
          <div className="form-grid">
            <label className="field">
              <span>Название кампании</span>
              <input
                value={newCampaignName}
                onChange={(e) => setNewCampaignName(e.target.value)}
                placeholder="Например, Летняя распродажа 2026"
              />
            </label>

            <label className="field">
              <span>Дата начала</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>

            <label className="field">
              <span>Дата окончания</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>

          <div className="cities-selector">
            <div className="cities-head">
              <span>Города показа</span>
              <b>{totalCitiesSelected} выбрано</b>
            </div>
            <div className="cities-grid">
              {AVAILABLE_CITIES_RU.map((city) => (
                <label key={city} className="city-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCities.includes(city)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCities([...selectedCities, city]);
                      } else {
                        setSelectedCities(selectedCities.filter((c) => c !== city));
                      }
                    }}
                  />
                  <span>{city[0]}</span>
                </label>
              ))}
            </div>
          </div>

          <button type="submit" className="create-btn" disabled={isLoading}>
            {isLoading ? 'Сохраняем...' : 'Создать кампанию'}
          </button>
        </form>

        {error && <div className="campaigns-error">{error}</div>}

        <div className="campaigns-table-wrap">
          {isLoading && campaigns.length === 0 ? (
            <div className="campaigns-empty">Загрузка...</div>
          ) : campaigns.length === 0 ? (
            <div className="campaigns-empty">Пока нет кампаний. Создайте первую выше.</div>
          ) : (
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th>Кампания</th>
                  <th>Период</th>
                  <th>Города</th>
                  <th>ID</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr key={campaign.publicId}>
                    <td className="campaign-name-cell">{campaign.name}</td>
                    <td>
                      <div className="period-cell">
                        <span>{formatDate(campaign.startDate)}</span>
                        <i>—</i>
                        <span>{formatDate(campaign.endDate)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="campaign-cities">
                        {(campaign.cityNames || []).filter(Boolean).join(', ') || 'Нет городов'}
                      </div>
                    </td>
                    <td className="campaign-id">{campaign.publicId}</td>
                    <td>
                      <button
                        type="button"
                        className="open-btn"
                        onClick={() => openCampaign(campaign)}
                      >
                        Открыть
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
